import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Line,
  LineChart,
  Cell,
} from "recharts";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";

function InfoCard({ title, value, subtitle, icon, accent }) {
  return (
    <Card sx={{ borderRadius: 3, height: "100%", borderLeft: `5px solid ${accent}` }} elevation={2}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="body2" color="text.secondary">{title}</Typography>
            <Typography variant="h5" fontWeight={700} mt={0.5}>{value}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
          </Box>
          <Box sx={{ color: accent }}>{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ClientDashboard() {
  const { userData } = useAuthStore();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const email = userData?.activeOrganizationUser?.email;
  const organizationId = userData?.organization?.organization_id || null;
  const roleLabel = userData?.organizationuser?.user_types?.user_type_name || "Client";

  useEffect(() => {
    let isMounted = true;
    const fetchDashboard = async () => {
      if (!organizationId) {
        setError("Organization not found in user profile.");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${MAIN_URL}/api/project-management/project/client-dashboard`, {
          headers: { ...(token && { Authorization: `Bearer ${token}` }) },
          params: { organization_id: organizationId, email },
        });
        if (isMounted) setDashboardData(res?.data?.data || null);
      } catch (err) {
        if (isMounted) {
          setDashboardData(null);
          setError(err?.response?.data?.message || err?.message || "Failed to load dashboard data.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchDashboard();
    return () => {
      isMounted = false;
    };
  }, [email, organizationId]);

  const summary = dashboardData?.summary || {};
  const clientsBreakdown = dashboardData?.clients_breakdown || [];
  const statusBreakdown = dashboardData?.status_breakdown || [];
  const priorityBreakdown = dashboardData?.priority_breakdown || [];
  const monthlyTrend = dashboardData?.monthly_trend || [];
  const upcomingTasks = dashboardData?.upcoming_tasks || [];
  const recentTasks = dashboardData?.recent_tasks || [];
  const pieColors = ["#1976d2", "#2e7d32", "#ed6c02", "#9c27b0", "#0288d1", "#d32f2f"];
  const topClient = useMemo(() => clientsBreakdown[0] || null, [clientsBreakdown]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} mb={1}>Client Dashboard</Typography>
      <Typography color="text.secondary" mb={3}>Welcome back. Track portfolio progress and delivery insights.</Typography>

      <Grid container spacing={2.5} mb={3}>
        
        <Grid item xs={12} md={6} lg={3}>
          <InfoCard title="Associated Clients" value={loading ? "..." : clientsBreakdown.length} subtitle="Linked to your email" icon={<GroupOutlinedIcon />} accent="#2e7d32" />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <InfoCard title="Total Projects" value={summary.total_projects ?? 0} subtitle={`${summary.active_projects ?? 0} active`} icon={<FolderOpenOutlinedIcon />} accent="#ed6c02" />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <InfoCard title="Total Tasks" value={summary.total_tasks ?? 0} subtitle={`Completion ${summary.completion_rate ?? 0}%`} icon={<TaskAltOutlinedIcon />} accent="#9c27b0" />
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 3 }} elevation={2}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} mb={1}>Quick Actions</Typography>
          {loading && (
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading dashboard...</Typography>
            </Stack>
          )}
          {!loading && error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={2}>
            <Button component={RouterLink} to="/project-reports" variant="contained" endIcon={<LaunchIcon />}>Open Project Reports</Button>
       
            <Button component={RouterLink} to="/client" variant="outlined">View Clients Overview</Button>
          </Stack>
          {!loading && clientsBreakdown.length > 0 && (
            <Box>
              <Typography variant="subtitle2" mb={1}>Client portfolio</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {clientsBreakdown.map((client) => (
                  <Chip
                    key={client.organization_client_id}
                    label={`${client.client_name || `Client #${client.organization_client_id}`} (${client.project_count})`}
                    component={RouterLink}
                    to={`/client/info/${client.organization_client_id}`}
                    clickable
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}
          {topClient && (
            <Typography variant="caption" color="text.secondary" display="block" mt={2}>
              Top client by projects: {topClient.client_name || topClient.organization_client_id}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Grid
        container
        spacing={2.5}
        mt={1}
        width="100%"
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            width: { xs: '100%', md: '45%' },
            flexBasis: { xs: '100%', md: '45%' },
            maxWidth: { xs: '100%', md: '45%' },
          }}
        >
          <Card sx={{ borderRadius: 3, height: 360, width: '100%' }} elevation={2}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" fontWeight={700} mb={1}>
                Monthly Delivery Trend
              </Typography>
              <ResponsiveContainer width="100%" height="88%">
                <LineChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month_label" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="projects_started"
                    name="Projects Started"
                    stroke="#1976d2"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="tasks_scheduled"
                    name="Tasks Scheduled"
                    stroke="#ed6c02"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="tasks_completed"
                    name="Tasks Completed"
                    stroke="#2e7d32"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            width: { xs: '100%', md: '45%' },
            flexBasis: { xs: '100%', md: '45%' },
            maxWidth: { xs: '100%', md: '45%' },
          }}
        >
          <Card sx={{ borderRadius: 3, height: 360, width: '100%' }} elevation={2}>
            <CardContent sx={{ height: '100%' }}>
              <Typography variant="h6" fontWeight={700} mb={1}>
                Tasks by Status
              </Typography>
              <ResponsiveContainer width="100%" height="88%">
                <PieChart>
                  <Pie
                    data={statusBreakdown}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={110}
                    label
                  >
                    {statusBreakdown.map((_, index) => (
                      <Cell key={`status-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            width: { xs: '100%', md: '45%' },
            flexBasis: { xs: '100%', md: '45%' },
            maxWidth: { xs: '100%', md: '45%' },
          }}
        >
          <Card sx={{ borderRadius: 3, minHeight: 330, width: '100%' }} elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={1}>
                Priority Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={priorityBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="priority" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#9c27b0" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            width: { xs: '100%', md: '45%' },
            flexBasis: { xs: '100%', md: '45%' },
            maxWidth: { xs: '100%', md: '45%' },
          }}
        >
         
        </Grid>
        <Grid item xs={12} sx={{ width: '100%' }}>
          <Stack spacing={3}>
            {/* Upcoming Tasks Card */}
            <Card sx={{ borderRadius: 4, width: '100%', bgcolor: 'background.paper', boxShadow: 4 }} elevation={0}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      bgcolor: 'primary.light',
                      color: 'primary.main',
                      borderRadius: '50%',
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <span role="img" aria-label="Upcoming">🗓️</span>
                  </Box>
                  <Typography variant="h6" fontWeight={700} component="div">
                    Upcoming Tasks
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Next scheduled tasks across client projects
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ minHeight: 56, mb: 1 }}
                >
                  {upcomingTasks.slice(0, 8).map((task) => (
                    <Chip
                      key={task.task_id}
                      label={
                        <Box component="span">
                          <b>{task.task_title || 'Task'}</b>
                          {`  |  `}
                          <span style={{ color: "#666" }}>{task.project_name || 'Project'}</span>
                          {`  |  `}
                          <span style={{ color: "#888" }}>{task.scheduled_date || '-'}</span>
                          
                        </Box>
                      }
                      variant="filled"
                      color={
                        task.status === 'Completed'
                          ? 'success'
                          : task.status === 'In Progress'
                          ? 'warning'
                          : 'default'
                      }
                      sx={{
                        fontWeight: 500,
                        background: "#f9f9fc",
                        border: "1px solid #e0e0ef",
                        '.MuiChip-label': { whiteSpace: "pre-line" }
                      }}
                    />
                  ))}
                  {upcomingTasks.length === 0 && (
                    <Typography variant="body2" color="text.secondary" alignSelf="center">
                      No upcoming tasks found.
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Recently Updated Tasks Card */}
            <Card sx={{ borderRadius: 4, width: '100%', bgcolor: 'background.paper', boxShadow: 4 }} elevation={0}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      bgcolor: 'secondary.light',
                      color: 'secondary.main',
                      borderRadius: '50%',
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <span role="img" aria-label="Updates">📝</span>
                  </Box>
                  <Typography variant="h6" fontWeight={700} component="div">
                    Recently Completed Tasks
                  </Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ minHeight: 56, mb: 1 }}
                >
                  {recentTasks.slice(0, 8).map((task) => (
                    <Chip
                      key={task.task_id}
                      label={
                        <Box component="span">
                          <b style={{ color: "#666" }}>{task.task_title || 'Task'}</b>
                          {`  |  `}
                          <span style={{ color: "#666" }}>{task.project_name || 'Project'}</span>
                          {`  |  `}
                          <span style={{ color: "#888" }}>{task.status || 'N/A'}</span>
                        </Box>
                      }
                      variant="filled"
                      color={
                        task.status === 'Completed'
                          ? 'success'
                          : task.status === 'In Progress'
                          ? 'warning'
                          : 'default'
                      }
                      sx={{
                        fontWeight: 500,
                        background: "#f9f9fc",
                        border: "1px solid #e0e0ef",
                        '.MuiChip-label': { whiteSpace: "pre-line" }
                      }}
                    />
                  ))}
                  {recentTasks.length === 0 && (
                    <Typography variant="body2" color="text.secondary" alignSelf="center">
                      No completed task  available.
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
 
      </Grid>
    </Box>
  );
}
